// ToastNotification.js
import Toastify from 'toastify-js';
import "toastify-js/src/toastify.css"

class Toast {
    #options = {};
    #toastInstance = null; // To keep track of the active Toastify instance's element

    // Default options for the toast
    static DEFAULT_OPTIONS = {
        message: "Default message",
        node: null,
        type: "info", // 'error', 'warning', 'success', 'info'
        duration: 3000, // 0 for sticky
        url: undefined,
        onClick: () => {},
        onDisappear: () => {},
        picture: undefined,
        position: "right", // 'left', 'center', 'right'
        from: "top",       // 'top', 'bottom'
        closeButton: true,
        style: {},
        className: "",
    };

    // Mapping types to predefined styles
    static TYPE_STYLES = {
        error: {
            background: "rgb(202, 0, 17)",
            color: "white"
        },
        warning: {
            background: "rgb(255, 128, 24)",
            color: "white"
        },
        success: {
            background: "#15632C",
            color: "white"
        },
        info: {
            background: "#82C989",
            color: "#333333"
        },
    };

    constructor(options = {}) {
        if (typeof Toastify !== 'function') {
            throw new Error("Toastify module not loaded or invalid.");
        }

        this.#options = { ...Toast.DEFAULT_OPTIONS, ...options };
        this.#applyTypeSpecifics();
    }

    #applyTypeSpecifics() {
        const typeStyle = Toast.TYPE_STYLES[this.#options.type] || {};
        // Merge type-specific styles, allowing user-provided style to override
        this.#options.style = { ...typeStyle, ...(this.#options.style || {}) };
        // If type defines a className and user hasn't provided one, use it.
        // If both define, concatenate.
        if (typeStyle.className && !this.#options.className) {
            this.#options.className = typeStyle.className;
        } else if (typeStyle.className && this.#options.className && !this.#options.className.includes(typeStyle.className)) {
            this.#options.className = `${typeStyle.className} ${this.#options.className}`;
        }
    }

    #prepareToastifyOptions() {
        const toastifyOpts = {
            text: this.#options.node ? undefined : this.#options.message,
            node: this.#options.node,
            duration: this.#options.duration === 0 ? -1 : this.#options.duration,
            destination: this.#options.url,
            newWindow: !!this.#options.url,
            close: this.#options.closeButton,
            gravity: this.#options.from,
            position: this.#options.position,
            avatar: this.#options.picture,
            className: this.#options.className,
            stopOnFocus: true,
            onClick: this.#options.onClick,
            offset: {
                x: this.#options.position === 'center' ? 0 : 20,
                y: 20
            },
            callback: () => {
                if (this.#options.onDisappear) {
                    this.#options.onDisappear();
                }
                this.#toastInstance = null;
            },
            style: this.#options.style,
        };

        if (!toastifyOpts.node && !toastifyOpts.text) {
            toastifyOpts.text = " "; // Toastify requires text or node
        }
        
        return toastifyOpts;
    }

    show() {
        if (this.#toastInstance) {
            this.hide();
        }

        const toastifyParams = this.#prepareToastifyOptions();
        const currentToastifyInstance = Toastify(toastifyParams);
        currentToastifyInstance.showToast();
        this.#toastInstance = currentToastifyInstance.toastElement;
        return this;
    }

    hide() {
        if (this.#toastInstance && this.#toastInstance.parentNode) {
            this.#toastInstance.parentNode.removeChild(this.#toastInstance);
            if (this.#options.onDisappear && this.#toastInstance !== null) {
                this.#options.onDisappear();
            }
            this.#toastInstance = null;
        }

        return this;
    }

    setOptions(newOptions = {}) {
        const oldOnClick = this.#options.onClick;
        const oldOnDisappear = this.#options.onDisappear;

        this.#options = { ...this.#options, ...newOptions };

        if (newOptions.onClick === undefined && oldOnClick) this.#options.onClick = oldOnClick;
        if (newOptions.onDisappear === undefined && oldOnDisappear) this.#options.onDisappear = oldOnDisappear;

        if (newOptions.type || newOptions.style || newOptions.className) {
            this.#applyTypeSpecifics();
        }

        if (this.#toastInstance) {
            // Hide current and show new one with updated options
            this.show();
        }

        return this;
    }

    getOptions() {
        return { ...this.#options };
    }
}

export default Toast;